import styled from "@emotion/styled";
import {
  BACKGROUND_LIGHT,
  BLUE_LIGHT,
  PRIMARY_BLACK,
  PRIMARY_BLUE,
  PRIMARY_GREY,
  TERTIARY_GREY,
} from "src/constants/colors";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background-color: #fafafb;
  overflow: hidden;
`;

export const Header = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 40px;
  height: 64px;
  flex-shrink: 0;
  border-bottom: 1px solid #e4e7ec;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 13.5px;
  height: 13.5px;
  color: #1453f5;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const HeaderTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.096px;
  color: ${PRIMARY_BLACK};
  white-space: nowrap;
`;

export const HeaderCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  border-radius: 99px;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  box-shadow: 0px 1px 0px 0px inset rgba(255, 255, 255, 0.2);
  color: #1453f5;

  &:hover {
    background: rgba(20, 83, 245, 0.08);
  }

  &:active {
    background: rgba(20, 83, 245, 0.12);
    box-shadow: 0px 1px 0px 0px inset rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #1453f5;
    outline-offset: 2px;
  }
`;

export const HeaderCloseButtonLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.072px;
`;

export const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: auto;
`;

export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

export const Content = styled.div`
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: auto;
  align-items: center;
  padding: 96px 24px;
  width: 100%;
`;

export const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  margin-bottom: 48px;
  font-size: 28px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.168px;
  color: ${PRIMARY_BLACK};
  flex-shrink: 0;
`;

export const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 628px;
  flex-shrink: 0;

  input {
    padding-left: 48px;
  }
`;

export const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  margin-top: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${TERTIARY_GREY};
  pointer-events: none;
  z-index: 3;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const LocationEmoji = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  background-color: ${BLUE_LIGHT};
  border-radius: 24px;
  color: #3265ea;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const LocationListWrapper = styled.div`
  width: 100%;
  max-width: 628px;
  padding-bottom: 80px;
  display: flex;
  flex-direction: column;
`;

export const LocationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const FadeOverlay = styled.div<{
  isVisible: boolean;
  position: "top" | "bottom";
}>`
  position: sticky;
  ${({ position }) => (position === "top" ? "top: 0;" : "bottom: 0;")}
  left: 0;
  right: 0;
  height: 80px;
  min-height: 80px;
  background: ${({ position }) =>
    position === "top"
      ? "linear-gradient(to bottom, rgba(250, 250, 251, 1), rgba(250, 250, 251, 0))"
      : "linear-gradient(to bottom, rgba(250, 250, 251, 0), rgba(250, 250, 251, 1))"};
  pointer-events: none;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 300ms ease-in-out;
  z-index: 1;
`;

export const BottomFadeOverlay = styled(FadeOverlay)`
  position: fixed;
`;

export const ChevronIcon = styled.div<{
  isSelected?: boolean;
  isHovered?: boolean;
  isActive?: boolean;
  isFocused?: boolean;
}>`
  margin-left: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  opacity: ${({ isSelected, isHovered, isActive, isFocused }) =>
    isSelected || isHovered || isActive || isFocused ? 0 : 1};
  transition: opacity 150ms ease-in-out;
`;

export const LocationCard = styled.div<{
  isHovered: boolean;
  isSelected: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px;
  background-color: ${({ isSelected }) =>
    isSelected ? BLUE_LIGHT : PRIMARY_GREY};
  border: 2px solid
    ${({ isSelected }) => (isSelected ? PRIMARY_BLUE : "transparent")};
  border-radius: 16px;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  height: 80px;
  box-sizing: border-box;

  &:hover {
    background-color: ${BLUE_LIGHT};
  }

  &:focus,
  &:active {
    background-color: ${BLUE_LIGHT};
    border-color: ${PRIMARY_BLUE};
  }
`;

export const LocationIcon = styled.div<{ hasLogo?: boolean }>`
  box-sizing: border-box;
  width: 48px;
  height: ${({ hasLogo }) => (hasLogo ? "auto" : "48px")};
  margin-right: 16px;
  flex-shrink: 0;
  background-color: ${({ hasLogo }) =>
    hasLogo ? "transparent" : BACKGROUND_LIGHT};
  border-radius: ${({ hasLogo }) => (hasLogo ? "0" : "50%")};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ hasLogo }) => (hasLogo ? "0" : "7px")};
  overflow: hidden;

  img {
    width: 48px;
    height: auto;
    object-fit: contain;
  }
`;

export const LocationInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-width: 0;
  height: 48px;
  justify-content: center;
`;

export const LocationName = styled.div<{ isSelected?: boolean }>`
  font-size: 16px;
  font-weight: 500;
  color: ${PRIMARY_BLACK};
  line-height: 20px;
  letter-spacing: -0.096px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LocationAddress = styled.div<{ isSelected?: boolean }>`
  font-size: 12px;
  font-weight: 400;
  color: ${TERTIARY_GREY};
  line-height: 16px;
  letter-spacing: -0.072px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
